import { useMutation, useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Leaf,
  Loader2,
  Lock,
  Mail,
  PackageCheck,
  ShieldCheck,
  Truck,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiClientError } from '@terra/api-client';
import { toast } from '@terra/ui';

import { api } from '../lib/api';
import { isVendor, useAuthStore } from '../stores/authStore';

const STOREFRONT_PREVIEW_IMAGE = '/storefront-preview.png';

const TERRA_LOGO_URL =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuCSmwtT-Za5b9CHsGaDhtFOTNwsZDaQldEF8lQZ9Elh4pjiafSgFIIoEK2sZDeGepJNhatnt0A_-cCJRnD1Lgx8lN4TeB8y-NrWrDTqDHyQ8QQoyco8I5YgDKEo7fb0MVYXSXbMZ82Qks4VxtGbNijakpYmro5fgKJzAtGyw5uVHt_bz5FiuQ8ACF1dRmYGcyAWcFBespTd8qFoUqwf5GTMjMJfjpSQuvXkum-3rTBxqpDGQlT4XoidUfRPUDjo4_vnKJE7Dt2tzrI';

const RESEND_COOLDOWN_SECONDS = 30;

/** Vendor Platform Pillars replacing static numbers */
const vendorPillars = [
  {
    title: 'Catalog Control',
    desc: 'Instant publishing & inventory sync',
    icon: PackageCheck,
  },
  {
    title: 'Order Fulfillment',
    desc: 'Automated tracking & shipping labels',
    icon: Truck,
  },
  {
    title: 'Direct Settlements',
    desc: 'Transparent payouts & ledger',
    icon: Wallet,
  },
];

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.09,
      delayChildren: 0.15,
    },
  },
};

export function LoginPage() {
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const setSession = useAuthStore((s) => s.setSession);
  const logout = useAuthStore((s) => s.logout);

  const { data: authLayoutSetting } = useQuery({
    queryKey: ['website-public-setting', 'auth_layout'],
    queryFn: () => api.cms.getPublicSetting('auth_layout').catch(() => null),
    staleTime: 60_000,
  });
  const bgImage = (authLayoutSetting?.value as any)?.adminLoginImage || STOREFRONT_PREVIEW_IMAGE;

  /**
   * Seller sign-in is two factors:
   * 1. credentials — email + password, checked by `/auth/vendor/login/start`
   * 2. otp — 6-digit SMS code redeemed at `/auth/vendor/login/verify`
   */
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const startLogin = useMutation({
    mutationFn: () => api.auth.vendorLoginStart({ email: email.trim(), password }),
    onSuccess: (res) => {
      setChallengeToken(res.challengeToken);
      setSentTo(res.sentTo);
      setOtpCode('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setStep('otp');
    },
  });

  const verifyLogin = useMutation({
    mutationFn: () =>
      api.auth.vendorLoginVerify({ challengeToken: challengeToken ?? '', code: otpCode.trim() }),
    onSuccess: async (res) => {
      if (!isVendor(res.user)) {
        setDenied(true);
        toast.error('This account is not registered as a vendor.');
        await logout();
        return;
      }
      setPassword('');
      toast.success('Successfully logged in.');
      setSession(res);
      navigate('/', { replace: true });
    },
  });

  const backToCredentials = () => {
    setStep('credentials');
    setOtpCode('');
    setChallengeToken(null);
    setPassword('');
    setDenied(false);
    startLogin.reset();
    verifyLogin.reset();
  };

  const activeMutation = step === 'credentials' ? startLogin : verifyLogin;
  const expired =
    verifyLogin.error instanceof ApiClientError && verifyLogin.error.code === 'TOKEN_EXPIRED';
  const error = denied
    ? 'This account is not registered as a vendor.'
    : activeMutation.error instanceof ApiClientError
      ? activeMutation.error.message
      : activeMutation.isError
        ? step === 'credentials'
          ? 'Could not sign in. Please check your email and password.'
          : 'Verification failed. Please check your OTP code.'
        : null;

  const loading = startLogin.isPending || verifyLogin.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setDenied(false);

    if (step === 'credentials') {
      if (!email.trim() || !password) return;
      startLogin.mutate();
    } else {
      if (otpCode.trim().length !== 6) return;
      verifyLogin.mutate();
    }
  };

  const motionProps = shouldReduceMotion
    ? {
        initial: false,
        animate: false,
      }
    : {};

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#F5F1E9]">
      {/* ── Background Ambient Glow Spheres ── */}
      {!shouldReduceMotion && (
        <>
          <motion.div
            className="absolute -right-24 -top-32 h-[420px] w-[420px] rounded-full bg-[#D7E2C4]/40 blur-3xl"
            animate={{
              scale: [1, 1.08, 1],
              opacity: [0.45, 0.7, 0.45],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          <motion.div
            className="absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full bg-[#EAC98B]/25 blur-3xl"
            animate={{
              scale: [1, 1.12, 1],
              opacity: [0.35, 0.6, 0.35],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </>
      )}

      {/* ── Decorative Leaf Vectors ── */}
      <motion.div
        className="pointer-events-none absolute right-10 top-8 text-[#80945B]/20"
        animate={
          shouldReduceMotion
            ? {}
            : {
                rotate: [0, 4, -3, 0],
                y: [0, 8, 0],
              }
        }
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <Leaf size={180} strokeWidth={0.8} />
      </motion.div>

      <motion.div
        className="pointer-events-none absolute bottom-0 left-4 text-[#A65A35]/10"
        animate={
          shouldReduceMotion
            ? {}
            : {
                rotate: [0, -5, 4, 0],
                y: [0, -10, 0],
              }
        }
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <Leaf size={210} strokeWidth={0.8} />
      </motion.div>

      {/* ── Main Container Card ── */}
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-8">
        <motion.div
          {...motionProps}
          initial={{
            opacity: 0,
            scale: 0.97,
            y: 25,
          }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
          }}
          transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="grid min-h-[720px] w-full max-w-[1250px] overflow-hidden rounded-[28px] border border-[#E9E2D7] bg-white shadow-[0_30px_90px_rgba(62,47,33,0.15)] lg:grid-cols-2"
        >
          {/* ═════════════════════════════════════════════════
              LEFT BRAND PANEL
          ══════════════════════════════════════════════════ */}
          <section className="relative overflow-hidden bg-[#F7F0E4]">
            {/* Background texture */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,.8),transparent_35%),radial-gradient(circle_at_90%_90%,rgba(122,145,83,.18),transparent_35%)]" />

            {/* Storefront UI Screenshot Preview */}
            <motion.div
              className="absolute right-0 top-0 h-full w-[56%] overflow-hidden"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 1.1,
                delay: 0.15,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <motion.img
                src={bgImage}
                alt="Terra Shoppy Storefront Preview"
                className="h-full w-full object-cover object-left-top shadow-2xl"
                animate={
                  shouldReduceMotion
                    ? {}
                    : {
                        scale: [1, 1.035, 1],
                      }
                }
                transition={{
                  duration: 12,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Smooth Image Overlays */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#F7F0E4] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3D321F]/30 via-transparent to-transparent" />
            </motion.div>

            {/* Left Content */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="relative z-10 flex h-full flex-col justify-between p-10 lg:p-14"
            >
              {/* Logo */}
              <motion.div variants={fadeUp}>
                <img
                  src={TERRA_LOGO_URL}
                  alt="Terra Shoppy"
                  className="h-10 w-auto max-w-[170px] object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/terra-shoppy-logo.png';
                  }}
                />
              </motion.div>

              {/* Main Copy */}
              <div className="mb-8 mt-auto max-w-[480px]">
                <motion.div variants={fadeUp} className="mb-4 inline-flex items-center gap-2">
                  <span className="h-px w-8 bg-[#B67A28]" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#8B6840]">
                    Vendor Portal
                  </span>
                </motion.div>

                <motion.h1
                  variants={fadeUp}
                  className="font-serif text-[42px] leading-[1.02] tracking-[-0.04em] text-[#35281D] lg:text-[50px]"
                >
                  Grow your
                  <br />
                  <span className="text-[#B47A25]">store &amp; sales</span>
                  <br />
                  with confidence.
                </motion.h1>

                <motion.p
                  variants={fadeUp}
                  className="mt-5 max-w-[390px] text-[15px] leading-7 text-[#6F6256]"
                >
                  Manage inventory, track customer orders, view settlements, and expand your reach
                  on Terra Shoppy.
                </motion.p>

                {/* Core Capabilities replacing static numbers */}
                <motion.div variants={fadeUp} className="mt-8 grid max-w-[470px] gap-4">
                  {vendorPillars.map((pillar) => {
                    const Icon = pillar.icon;

                    return (
                      <motion.div
                        key={pillar.title}
                        whileHover={
                          shouldReduceMotion
                            ? {}
                            : {
                                x: 4,
                              }
                        }
                        transition={{
                          duration: 0.2,
                        }}
                        className="flex items-center gap-3.5 rounded-2xl border border-[#E8DCC9]/80 bg-white/70 p-3.5 shadow-sm backdrop-blur-sm"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#6E8449]/10 text-[#6E8449]">
                          <Icon size={20} />
                        </div>
                        <div>
                          <div className="text-[14px] font-semibold text-[#34271C]">
                            {pillar.title}
                          </div>
                          <div className="text-[12px] text-[#76695B]">{pillar.desc}</div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </div>

              {/* Bottom Badge */}
              <motion.div variants={fadeUp} className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#6E8449] text-white">
                  <Leaf size={18} />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#4D4238]">
                    Natural • Authentic • Better Living
                  </p>
                  <p className="mt-0.5 text-xs text-[#8C8175]">Empowering sellers everywhere.</p>
                </div>
              </motion.div>
            </motion.div>
          </section>

          {/* ═════════════════════════════════════════════════
              RIGHT LOGIN PANEL (LOGO REPLACING WELCOME SELLER)
          ══════════════════════════════════════════════════ */}
          <section className="relative flex items-center justify-center bg-white px-7 py-12 lg:px-16">
            <motion.div
              initial={{
                opacity: 0,
                x: 25,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.75,
                delay: 0.25,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="w-full max-w-[430px]"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="mb-6 inline-flex items-center rounded-full bg-[#F9E7E4] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-[#B63D36]"
              >
                {step === 'credentials' ? 'Seller Console' : 'SMS Verification'}
              </motion.div>

              {/* Logo replacing Welcome Seller heading in Step 1 */}
              {step === 'credentials' ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                  className="mb-4"
                >
                  <img
                    src={TERRA_LOGO_URL}
                    alt="Terra Shoppy Logo"
                    className="h-12 w-auto max-w-[210px] object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/terra-shoppy-logo.png';
                    }}
                  />
                </motion.div>
              ) : (
                <h2 className="font-serif text-[38px] leading-[1.05] tracking-[-0.035em] text-[#182033]">
                  Verify mobile code
                </h2>
              )}

              <p className="mt-2 text-[15px] text-[#727989]">
                {step === 'credentials'
                  ? 'Sign in to access your store and orders.'
                  : `Enter the 6-digit SMS code sent to ${sentTo ?? 'your registered mobile'}.`}
              </p>

              {/* FORM */}
              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                {step === 'credentials' ? (
                  <>
                    {/* Email */}
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.55 }}
                    >
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-semibold text-[#252B38]"
                      >
                        Registered email address
                      </label>
                      <div className="group relative">
                        <Mail
                          size={18}
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9AA0AB] transition-colors group-focus-within:text-[#B63D36]"
                        />
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="store@example.com"
                          required
                          className="h-[52px] w-full rounded-xl border border-[#E0E3E8] bg-[#FCFCFD] pl-12 pr-4 text-[#202633] outline-none transition-all duration-200 placeholder:text-[#A8AFBA] focus:border-[#C34B43] focus:bg-white focus:ring-4 focus:ring-[#C34B43]/10"
                        />
                      </div>
                    </motion.div>

                    {/* Password */}
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.63 }}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <label
                          htmlFor="password"
                          className="block text-sm font-semibold text-[#252B38]"
                        >
                          Password
                        </label>
                        <button
                          type="button"
                          onClick={() => navigate('/forgot-password')}
                          className="text-xs font-medium text-[#C13D37] transition hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="group relative">
                        <Lock
                          size={18}
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9AA0AB] transition-colors group-focus-within:text-[#B63D36]"
                        />
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          className="h-[52px] w-full rounded-xl border border-[#E0E3E8] bg-[#FCFCFD] pl-12 pr-12 text-[#202633] outline-none transition-all duration-200 placeholder:text-[#A8AFBA] focus:border-[#C34B43] focus:bg-white focus:ring-4 focus:ring-[#C34B43]/10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[#8E95A1] transition hover:bg-[#F8F1F0] hover:text-[#B63D36]"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          <AnimatePresence mode="wait">
                            {showPassword ? (
                              <motion.div
                                key="eye-off"
                                initial={{ opacity: 0, scale: 0.7 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.7 }}
                              >
                                <EyeOff size={18} />
                              </motion.div>
                            ) : (
                              <motion.div
                                key="eye"
                                initial={{ opacity: 0, scale: 0.7 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.7 }}
                              >
                                <Eye size={18} />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </button>
                      </div>
                    </motion.div>
                  </>
                ) : (
                  <>
                    {/* OTP Input */}
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <label
                        htmlFor="otpCode"
                        className="mb-2 block text-sm font-semibold text-[#252B38]"
                      >
                        6-Digit Security Code
                      </label>
                      <div className="group relative">
                        <input
                          id="otpCode"
                          name="otpCode"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={otpCode}
                          onChange={(e) =>
                            setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))
                          }
                          placeholder="000000"
                          autoFocus
                          required
                          className="h-[56px] w-full rounded-xl border border-[#E0E3E8] bg-[#FCFCFD] text-center font-mono text-2xl font-bold tracking-[8px] text-[#202633] outline-none transition-all duration-200 focus:border-[#C34B43] focus:bg-white focus:ring-4 focus:ring-[#C34B43]/10"
                        />
                      </div>
                    </motion.div>

                    {/* OTP Action Buttons */}
                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => startLogin.mutate()}
                        disabled={startLogin.isPending || cooldown > 0 || !password}
                        className="font-medium text-[#C13D37] transition hover:underline disabled:opacity-50"
                      >
                        {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                      </button>
                      <button
                        type="button"
                        onClick={backToCredentials}
                        className="font-medium text-[#727989] transition hover:text-[#252B38] hover:underline"
                      >
                        Use different email
                      </button>
                    </div>
                  </>
                )}

                {/* Error Banner */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{
                        opacity: 0,
                        height: 0,
                        y: -5,
                      }}
                      animate={{
                        opacity: 1,
                        height: 'auto',
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        height: 0,
                      }}
                      transition={{
                        duration: 0.25,
                      }}
                      className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                    >
                      <AlertCircle size={17} className="shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={
                    loading ||
                    (step === 'credentials'
                      ? !email.trim() || !password
                      : otpCode.trim().length !== 6)
                  }
                  whileHover={
                    loading || shouldReduceMotion
                      ? {}
                      : {
                          y: -2,
                          scale: 1.01,
                          boxShadow: '0 14px 30px rgba(190,62,55,0.20)',
                        }
                  }
                  whileTap={
                    loading || shouldReduceMotion
                      ? {}
                      : {
                          scale: 0.985,
                        }
                  }
                  transition={{
                    duration: 0.2,
                  }}
                  className="flex h-[54px] w-full items-center justify-center gap-2 rounded-xl bg-[#C84740] font-semibold tracking-[0.01em] text-white shadow-[0_8px_20px_rgba(190,62,55,0.14)] transition-colors duration-200 hover:bg-[#B83C36] disabled:cursor-not-allowed disabled:bg-[#D98984]"
                >
                  <AnimatePresence mode="wait">
                    {loading ? (
                      <motion.div
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <Loader2 size={19} className="animate-spin" />
                        <span>
                          {step === 'credentials' ? 'Signing in...' : 'Verifying code...'}
                        </span>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="login"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <span>
                          {step === 'credentials'
                            ? 'Sign in to Vendor Console'
                            : 'Verify & Enter Console'}
                        </span>
                        <motion.span
                          animate={
                            shouldReduceMotion
                              ? {}
                              : {
                                  x: [0, 4, 0],
                                }
                          }
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            repeatDelay: 1,
                          }}
                        >
                          <ArrowRight size={19} />
                        </motion.span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </form>

              {/* Create Account Link */}
              {step === 'credentials' && (
                <div className="mt-6 text-center text-sm font-medium text-[#727989]">
                  New Seller?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="font-semibold text-[#C13D37] hover:underline"
                  >
                    Create Seller Account
                  </button>
                </div>
              )}

              {/* Security Banner */}
              <motion.div
                initial={{
                  opacity: 0,
                  y: 10,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: 0.9,
                }}
                className="mt-8"
              >
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-[#E9EBEF]" />
                  <div className="flex items-center gap-2 text-[#6C7481]">
                    <motion.div
                      animate={
                        shouldReduceMotion
                          ? {}
                          : {
                              scale: [1, 1.08, 1],
                            }
                      }
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                      }}
                    >
                      <ShieldCheck size={18} className="text-[#B17B28]" />
                    </motion.div>
                    <span className="text-xs font-medium">Protected seller portal</span>
                  </div>
                  <div className="h-px flex-1 bg-[#E9EBEF]" />
                </div>
                <p className="mt-2 text-center text-xs text-[#9AA0AA]">
                  Your data is secure and encrypted.
                </p>
              </motion.div>

              {/* Back Link */}
              {step === 'otp' && (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={backToCredentials}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#727989] hover:text-[#B63D36] hover:underline"
                  >
                    <ArrowLeft size={14} /> Back to seller login
                  </button>
                </div>
              )}

              {/* Footer */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-8 text-center text-xs text-[#9AA0AA]"
              >
                Terra Shoppy Seller Console
                <span className="mx-2">•</span>
                Secure access
              </motion.p>
            </motion.div>
          </section>
        </motion.div>
      </div>
    </main>
  );
}
